import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { getDashboardProgress, getRoles, getStreak, getJDPrepSessions, getDailyChallenge, completeDailyChallenge, getFeatureFlags, getMyEnrollments, getInterviews } from '../../services/userApi';
import ActivityHeatmap from '../../components/common/ActivityHeatmap';

// ── SVG Icons ──────────────────────────────────────────────────────────────────
function Icon({ name, size = 20, color }) {
  const icons = {
    flame: (
      <path d="M8.5 14.5A2.5 2.5 0 0011 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 11-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 002.5 2.5z" />
    ),
    target: (
      <>
        <circle cx="12" cy="12" r="10" />
        <circle cx="12" cy="12" r="6" />
        <circle cx="12" cy="12" r="2" />
      </>
    ),
    book: (
      <>
        <path d="M4 19.5A2.5 2.5 0 016.5 17H20" />
        <path d="M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z" />
      </>
    ),
    calendar: (
      <>
        <rect x="3" y="4" width="18" height="18" rx="2" />
        <line x1="16" y1="2" x2="16" y2="6" />
        <line x1="8" y1="2" x2="8" y2="6" />
        <line x1="3" y1="10" x2="21" y2="10" />
      </>
    ),
    sparkle: (
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    ),
    arrow: (
      <>
        <line x1="5" y1="12" x2="19" y2="12" />
        <polyline points="12 5 19 12 12 19" />
      </>
    ),
    star: (
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    ),
    check: <polyline points="20 6 9 17 4 12" />,
    trophy: (
      <>
        <path d="M6 9H4.5a2.5 2.5 0 010-5H6" />
        <path d="M18 9h1.5a2.5 2.5 0 000-5H18" />
        <path d="M4 22h16" />
        <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22" />
        <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22" />
        <path d="M18 2H6v7a6 6 0 0012 0V2z" />
      </>
    ),
    download: (
      <>
        <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
        <polyline points="7 10 12 15 17 10" />
        <line x1="12" y1="15" x2="12" y2="3" />
      </>
    ),
  };
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke={color || 'currentColor'} strokeWidth="1.75"
      strokeLinecap="round" strokeLinejoin="round">
      {icons[name]}
    </svg>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────────
function ProgressRing({ pct, size = 64, stroke = 6, color = '#1B3A5C' }) {
  const r    = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  return (
    <svg width={size} height={size} style={{ transform: 'rotate(-90deg)', flexShrink: 0 }}>
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#E2E8F0" strokeWidth={stroke} />
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color}
        strokeWidth={stroke} strokeDasharray={circ}
        strokeDashoffset={circ - (pct / 100) * circ}
        strokeLinecap="round" style={{ transition: 'stroke-dashoffset 0.8s ease' }} />
    </svg>
  );
}

function Skeleton({ h = 100, rounded = '12px' }) {
  return (
    <div style={{ height: h, borderRadius: rounded, overflow: 'hidden', background: '#fff', border: '1px solid #E2E8F0' }}>
      <div style={{
        height: '100%',
        background: 'linear-gradient(90deg,#F1F5F9 25%,#E2E8F0 50%,#F1F5F9 75%)',
        backgroundSize: '200% 100%', animation: 'shimmer 1.4s infinite',
      }} />
    </div>
  );
}

function timeOfDay() {
  const h = new Date().getHours();
  if (h < 12) return 'morning';
  if (h < 17) return 'afternoon';
  return 'evening';
}

const WEEKLY_GOAL = 3;

// ── Animated Counter ──────────────────────────────────────────────────────────
function AnimatedNumber({ value }) {
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    const target = typeof value === 'number' ? value : 0;
    if (target === 0) { setDisplay(0); return; }
    let start = 0;
    const step = Math.ceil(target / 24);
    const timer = setInterval(() => {
      start += step;
      if (start >= target) { setDisplay(target); clearInterval(timer); }
      else setDisplay(start);
    }, 30);
    return () => clearInterval(timer);
  }, [value]);
  return <>{display}</>;
}

// ── Daily Challenge Card ──────────────────────────────────────────────────────
function DailyChallengeCard({ challenge, done, selected, onSelect, onSubmit }) {
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    setSubmitting(true);
    try { await onSubmit(); } catch {}
    finally { setSubmitting(false); }
  };

  return (
    <div className="dash-card" style={{ background: '#fff', borderRadius: 12, overflow: 'hidden', border: '1px solid #E2E8F0', boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 4px 16px rgba(0,0,0,0.04)', animationDelay: '100ms' }}>
      <div style={{ padding: '12px 16px', background: 'linear-gradient(135deg,#1B3A5C,#0F2340)', display: 'flex', alignItems: 'center', gap: 10 }}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="#F59E0B" stroke="none"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>
        <div style={{ flex: 1 }}>
          <p style={{ margin: 0, fontSize: 12, fontWeight: 700, color: '#fff', letterSpacing: '0.04em', textTransform: 'uppercase' }}>Daily Challenge</p>
          <p style={{ margin: 0, fontSize: 11, color: 'rgba(255,255,255,0.5)' }}>{challenge.role_title} · {challenge.sc_title}</p>
        </div>
        {done && (
          <span style={{ fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 99, background: challenge.was_correct ? '#DCFCE7' : '#FEE2E2', color: challenge.was_correct ? '#166534' : '#991B1B' }}>
            {challenge.was_correct ? 'Correct ✓' : 'Incorrect'}
          </span>
        )}
      </div>
      <div style={{ padding: '14px 16px' }}>
        <p style={{ margin: '0 0 12px', fontSize: 13, fontWeight: 600, color: '#0F172A', lineHeight: 1.5 }}>
          {challenge.question_text}
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {(challenge.options || []).map((opt, i) => {
            const isSelected = selected === i;
            const isCorrect  = done && opt === challenge.correct_answer;
            const isWrong    = done && isSelected && !isCorrect;
            const bg = isCorrect ? '#F0FDF4' : isWrong ? '#FFF5F5' : isSelected ? '#EFF6FF' : '#F8FAFC';
            const border = isCorrect ? '1px solid #86EFAC' : isWrong ? '1px solid #FCA5A5' : isSelected ? '1px solid #93C5FD' : '1px solid #E2E8F0';
            const textColor = isCorrect ? '#166534' : isWrong ? '#991B1B' : isSelected ? '#1E3A8A' : '#374151';
            return (
              <button key={i} onClick={() => !done && onSelect(i)}
                style={{ padding: '8px 12px', borderRadius: 8, border, background: bg, color: textColor, fontSize: 13, fontWeight: isSelected || isCorrect ? 600 : 400, textAlign: 'left', cursor: done ? 'default' : 'pointer', transition: 'all 0.12s', fontFamily: 'inherit' }}>
                <span style={{ fontWeight: 700, marginRight: 6, color: isCorrect ? '#16A34A' : isWrong ? '#C1272D' : '#94A3B8' }}>
                  {String.fromCharCode(65 + i)}.
                </span>
                {opt}
              </button>
            );
          })}
        </div>
        {!done && (
          <button onClick={handleSubmit} disabled={selected === null || submitting}
            style={{ marginTop: 12, width: '100%', padding: '9px', background: selected === null ? '#F1F5F9' : '#1B3A5C', color: selected === null ? '#94A3B8' : '#fff', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: selected === null ? 'not-allowed' : 'pointer', fontFamily: 'inherit' }}>
            {submitting ? 'Submitting…' : 'Submit answer'}
          </button>
        )}
      </div>
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function UserDashboard() {
  const { user, subscriptions, refreshSubscriptions } = useAuth();
  const navigate = useNavigate();

  const [progress,       setProgress]       = useState([]);
  const [suggestedRoles, setSuggestedRoles] = useState([]);
  const [streak,         setStreak]         = useState(null);
  const [loading,        setLoading]        = useState(true);
  const [loadError,      setLoadError]      = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [jdSessions,     setJdSessions]     = useState([]);
  const [challenge,      setChallenge]      = useState(null);
  const [features,       setFeatures]       = useState({ daily_challenge: true, streak: true });
  const [challengeSel,   setChallengeSel]   = useState(null);
  const [challengeDone,  setChallengeDone]  = useState(false);
  const [enrollments,    setEnrollments]    = useState([]);
  const [upcomingIvs,    setUpcomingIvs]    = useState([]);

  const loadData = () => {
    setLoading(true);
    setLoadError(false);
    Promise.all([
      refreshSubscriptions().catch(() => {}),
      getDashboardProgress().then(r => setProgress(r.data)).catch(() => {}),
      getRoles().then(r => setSuggestedRoles(r.data.slice(0, 3))).catch(() => {}),
      getStreak().then(r => setStreak(r.data)).catch(() => {}),
      getJDPrepSessions().then(r => setJdSessions(r.data.filter(s => s.status === 'completed').slice(0, 3))).catch(() => {}),
      getFeatureFlags().then(r => setFeatures(r.data)).catch(() => {}),
      getMyEnrollments().then(r => setEnrollments(r.data.enrollments || [])).catch(() => {}),
      getInterviews().then(r => setUpcomingIvs(r.data.slice(0, 3))).catch(() => {}),
    ]).catch(() => setLoadError(true)).finally(() => setLoading(false));
  };

  useEffect(() => {
    if (features.daily_challenge) {
      getDailyChallenge().then(r => { setChallenge(r.data); if (r.data.completed) setChallengeDone(true); }).catch(() => {});
    }
  }, [features.daily_challenge]); // eslint-disable-line

  useEffect(() => { loadData(); }, []); // eslint-disable-line

  useEffect(() => {
    if (!loading && subscriptions.length === 0 && progress.length === 0) {
      if (!localStorage.getItem('ip_onboarded')) setShowOnboarding(true);
    }
  }, [loading, subscriptions, progress]);

  const weeklyCompleted = progress.reduce((acc, p) => acc + Math.min(p.sub_chapters_completed || 0, 1), 0);
  const weeklyPct       = Math.min(Math.round((weeklyCompleted / WEEKLY_GOAL) * 100), 100);
  const firstName       = user?.full_name?.split(' ')[0] || 'there';
  const currentStreak   = streak?.current_streak ?? 0;

  const STATS = [
    {
      label: 'Day streak', icon: 'flame', accentColor: '#C1272D', bg: '#FEE2E2',
      value: currentStreak, suffix: ' days',
      sub: streak?.longest_streak ? `Best: ${streak.longest_streak} days` : 'Start today',
      numeric: currentStreak,
    },
    {
      label: 'Weekly goal', icon: 'target', accentColor: '#1B3A5C', bg: '#EFF6FF',
      value: null, suffix: '',
      sub: weeklyPct >= 100 ? 'Goal reached!' : `${WEEKLY_GOAL - weeklyCompleted} session${WEEKLY_GOAL - weeklyCompleted !== 1 ? 's' : ''} to go`,
      bar: weeklyPct, weeklyLabel: `${weeklyCompleted}/${WEEKLY_GOAL}`,
      numeric: weeklyCompleted,
    },
    {
      label: 'Roles studied', icon: 'book', accentColor: '#7C3AED', bg: '#EDE9FE',
      value: progress.length, suffix: '',
      sub: `${subscriptions.length} subscribed`,
      numeric: progress.length,
    },
    {
      label: 'Days studied', icon: 'calendar', accentColor: '#2563EB', bg: '#EFF6FF',
      value: streak?.total_days ?? 0, suffix: '',
      sub: 'All time',
      numeric: streak?.total_days ?? 0,
    },
  ];

  return (
    <div style={{ background: 'var(--bg, #F8FAFC)', minHeight: '100vh', fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
        @keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
        @keyframes fadeUp  { from{opacity:0;transform:translateY(16px)} to{opacity:1;transform:none} }
        @keyframes scaleIn { from{opacity:0;transform:scale(0.96)} to{opacity:1;transform:scale(1)} }
        .dash-card { animation: fadeUp 0.4s ease both; }
        .dash-stat-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; margin-bottom: 24px; }
        .dash-two-col  { display: grid; grid-template-columns: 1fr 320px; gap: 24px; align-items: start; }
        /* Force grid items to respect their track width — prevents 728px heatmap expanding the column */
        .dash-two-col > * { min-width: 0; }
        .dash-col-left, .dash-col-right { min-width: 0; }
        @media (max-width: 900px) {
          .dash-stat-grid { grid-template-columns: repeat(2, 1fr); }
          .dash-two-col   { grid-template-columns: 1fr; }
        }
        @media (max-width: 540px) {
          .dash-outer { padding: 16px 12px !important; }
          .dash-card-header { padding: 12px 14px !important; }
          .dash-card-row { padding: 12px 14px !important; flex-wrap: wrap !important; }
          .dash-card-row-btn { margin-top: 8px; align-self: flex-end; }
          .dash-hero-inner { padding: 20px 18px !important; }
          .dash-hero-inner > a { width: 100% !important; justify-content: center !important; }
          .dash-hdr-long { display: none !important; }
          .dash-hdr-short { display: inline !important; }
        }
        @media (max-width: 480px) {
          .dash-stat-grid { grid-template-columns: 1fr 1fr; gap: 10px; }
        }
        @media (prefers-reduced-motion: reduce) { .dash-card { animation: none; } }
      `}</style>

      {/* ── Onboarding modal ──────────────────────────────────────────────── */}
      {showOnboarding && (
        <div
          role="dialog" aria-modal="true" aria-labelledby="onboarding-title"
          style={{ position: 'fixed', inset: 0, zIndex: 999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, background: 'rgba(15,23,42,0.6)', backdropFilter: 'blur(4px)' }}
          onClick={() => { localStorage.setItem('ip_onboarded','1'); setShowOnboarding(false); }}
          onKeyDown={e => { if (e.key === 'Escape') { localStorage.setItem('ip_onboarded','1'); setShowOnboarding(false); } }}>
          <div
            style={{ background: '#fff', borderRadius: 16, padding: 32, maxWidth: 420, width: '100%', textAlign: 'center', boxShadow: '0 24px 64px rgba(0,0,0,0.2)', animation: 'scaleIn 0.28s ease', position: 'relative' }}
            onClick={e => e.stopPropagation()}>
            <button
              onClick={() => { localStorage.setItem('ip_onboarded','1'); setShowOnboarding(false); }}
              aria-label="Close welcome dialog"
              style={{ position: 'absolute', top: 14, right: 14, background: 'none', border: 'none', cursor: 'pointer', color: '#94A3B8', padding: 4, borderRadius: 6, display: 'flex', transition: 'color 0.12s' }}
              onMouseEnter={e => e.currentTarget.style.color='#1B3A5C'}
              onMouseLeave={e => e.currentTarget.style.color='#94A3B8'}>
              <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
            <div style={{ width: 56, height: 56, borderRadius: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px', background: 'linear-gradient(135deg, #1B3A5C, #2A5280)' }}>
              <Icon name="star" size={24} color="#fff" />
            </div>
            <h2 id="onboarding-title" style={{ margin: '0 0 8px', fontSize: 22, fontWeight: 800, color: '#0F172A', letterSpacing: '-0.5px' }}>
              Welcome to CentricQ
            </h2>
            <p style={{ margin: '0 0 28px', fontSize: 14, color: '#475569', lineHeight: 1.75 }}>
              Browse 150+ job roles, practice with 3 answer formats, and get AI feedback on every answer.
              200 free questions — no card needed.
            </p>
            <button onClick={() => { localStorage.setItem('ip_onboarded','1'); setShowOnboarding(false); navigate('/roles'); }}
              style={{ width: '100%', background: '#C1272D', color: '#fff', border: 'none', borderRadius: 10, padding: '12px 20px', fontSize: 15, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', transition: 'background 0.15s' }}
              onMouseEnter={e => e.currentTarget.style.background='#A8202B'}
              onMouseLeave={e => e.currentTarget.style.background='#C1272D'}>
              Browse roles →
            </button>
          </div>
        </div>
      )}

      <div className="dash-outer" style={{ maxWidth: 1024, margin: '0 auto', padding: '32px 24px' }}>

        {/* ── Welcome banner ──────────────────────────────────────────────── */}
        <div className="dash-card" style={{
          background: 'linear-gradient(135deg, #0a1929 0%, #0d2238 45%, #1B3A5C 85%, #1e3d61 100%)',
          borderRadius: 12, marginBottom: 24, color: '#fff', position: 'relative', overflow: 'hidden',
          boxShadow: '0 4px 24px rgba(13,34,56,0.3)',
          animationDelay: '0ms',
        }}>
          <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', backgroundImage: 'linear-gradient(rgba(255,255,255,0.025) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,0.025) 1px,transparent 1px)', backgroundSize: '48px 48px' }} />
          <div style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: 280, pointerEvents: 'none', background: 'radial-gradient(ellipse at right center, rgba(193,39,45,0.18) 0%, transparent 65%)' }} />
          <div className="dash-hero-inner" style={{ position: 'relative', zIndex: 1, padding: '28px 32px', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 20 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{ margin: '0 0 4px', fontSize: 12, color: 'rgba(255,255,255,0.45)', fontWeight: 500, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                Good {timeOfDay()}
              </p>
              <h1 style={{ margin: '0 0 12px', fontSize: 32, fontWeight: 800, letterSpacing: '-0.5px', lineHeight: 1.1 }}>
                {firstName}
                {currentStreak > 3 && (
                  <span style={{ fontSize: 17, fontWeight: 600, color: 'rgba(255,255,255,0.55)', marginLeft: 12 }}>You're on a roll!</span>
                )}
              </h1>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {currentStreak > 0 && (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.14)', padding: '6px 12px', borderRadius: 8, fontSize: 13, fontWeight: 600 }}>
                    <Icon name="flame" size={13} color="#FCA5A5" />
                    {currentStreak} day streak — keep it alive!
                  </span>
                )}
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'rgba(255,255,255,0.07)', padding: '6px 12px', borderRadius: 8, fontSize: 13, color: 'rgba(255,255,255,0.55)' }}>
                  {subscriptions.length > 0
                    ? `${subscriptions.length} active subscription${subscriptions.length !== 1 ? 's' : ''}`
                    : '200 questions free — no card needed'}
                </span>
              </div>
            </div>
            <Link to="/roles"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.14)', color: '#fff', textDecoration: 'none', padding: '10px 20px', borderRadius: 8, fontSize: 14, fontWeight: 600, transition: 'background 0.15s', flexShrink: 0, marginTop: 4, cursor: 'pointer' }}
              onMouseEnter={e => e.currentTarget.style.background='rgba(255,255,255,0.18)'}
              onMouseLeave={e => e.currentTarget.style.background='rgba(255,255,255,0.1)'}>
              Browse roles <Icon name="arrow" size={14} />
            </Link>
          </div>
        </div>

        {/* ── Continue card — hidden until user has started at least one role ── */}
        {!loading && progress.length > 0 && (() => {
          const top       = progress[0];
          const done      = top.sub_chapters_completed || 0;
          const pct       = Math.min(Math.round((done / 40) * 100), 100);
          const remaining = 40 - done;
          return (
            <div className="dash-card" style={{ marginBottom: 24, animationDelay: '30ms' }}>
              <div style={{
                background: '#fff', borderRadius: 12,
                border: '1px solid #E2E8F0', borderLeft: '4px solid #1B3A5C',
                boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 4px 16px rgba(0,0,0,0.04)',
                padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap',
              }}>
                <div style={{ position: 'relative', flexShrink: 0, width: 52, height: 52 }}>
                  <ProgressRing pct={pct} size={52} stroke={5} color="#1B3A5C" />
                  <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <span style={{ fontSize: 10, fontWeight: 800, color: '#1B3A5C' }}>{pct}%</span>
                  </div>
                </div>
                <div style={{ flex: 1, minWidth: 180 }}>
                  <p style={{ margin: '0 0 2px', fontSize: 11, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Continue where you left off</p>
                  <p style={{ margin: '0 0 6px', fontSize: 15, fontWeight: 700, color: '#0F172A', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{top.role_title}</p>
                  <div style={{ height: 4, borderRadius: 999, overflow: 'hidden', background: '#F1F5F9', maxWidth: 240 }}>
                    <div style={{ height: '100%', borderRadius: 999, width: `${pct}%`, background: '#1B3A5C', transition: 'width 0.7s ease' }} />
                  </div>
                  <p style={{ margin: '4px 0 0', fontSize: 12, color: '#64748B' }}>
                    {done} of 40 sub-chapters · <span style={{ fontWeight: 600, color: '#1B3A5C' }}>{remaining} to go</span>
                  </p>
                </div>
                <Link to={`/roles/${top.slug}`}
                  style={{ flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: 6, background: '#1B3A5C', color: '#fff', textDecoration: 'none', padding: '11px 20px', borderRadius: 8, fontSize: 13, fontWeight: 700, transition: 'background 0.15s', cursor: 'pointer', minHeight: 44 }}
                  onMouseEnter={e => e.currentTarget.style.background='#142d47'}
                  onMouseLeave={e => e.currentTarget.style.background='#1B3A5C'}>
                  Resume <Icon name="arrow" size={13} />
                </Link>
              </div>
            </div>
          );
        })()}

        {/* ── Error state ─────────────────────────────────────────────────── */}
        {!loading && loadError && (
          <div style={{ background: '#FEE2E2', border: '1px solid #FCA5A5', borderRadius: 10, padding: '14px 20px', marginBottom: 20, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
            <p style={{ margin: 0, fontSize: 14, color: '#C1272D', fontWeight: 600 }}>Could not load your dashboard data.</p>
            <button onClick={loadData} style={{ flexShrink: 0, background: '#C1272D', color: '#fff', border: 'none', borderRadius: 7, padding: '7px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
              Retry
            </button>
          </div>
        )}

        {/* ── Stat cards ──────────────────────────────────────────────────── */}
        {loading ? (
          <div className="dash-stat-grid">
            {[1,2,3,4].map(i => <Skeleton key={i} h={130} />)}
          </div>
        ) : (
          <div className="dash-stat-grid">
            {STATS.map((s, idx) => (
              <div key={s.label} className="dash-card"
                style={{
                  background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: 12,
                  overflow: 'hidden', position: 'relative',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 4px 16px rgba(0,0,0,0.04)',
                  animationDelay: `${60 + idx * 60}ms`,
                  transition: 'box-shadow 0.2s, transform 0.2s',
                }}
                onMouseEnter={e => { e.currentTarget.style.boxShadow='0 4px 20px rgba(0,0,0,0.1)'; e.currentTarget.style.transform='translateY(-2px)'; }}
                onMouseLeave={e => { e.currentTarget.style.boxShadow='0 1px 3px rgba(0,0,0,0.06), 0 4px 16px rgba(0,0,0,0.04)'; e.currentTarget.style.transform='none'; }}>
                {/* Top accent bar */}
                <div style={{ height: 3, background: s.accentColor, position: 'absolute', top: 0, left: 0, right: 0, borderRadius: '12px 12px 0 0' }} />

                <div style={{ padding: '20px 18px 16px' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 12 }}>
                    <p style={{ margin: 0, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#475569' }}>{s.label}</p>
                    <div style={{ width: 34, height: 34, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, background: s.bg, color: s.accentColor }}>
                      <Icon name={s.icon} size={16} color={s.accentColor} />
                    </div>
                  </div>

                  <p style={{ margin: '0 0 2px', fontSize: 32, fontWeight: 800, lineHeight: 1, letterSpacing: '-1px', color: '#0F172A' }}>
                    {s.weeklyLabel
                      ? s.weeklyLabel
                      : typeof s.numeric === 'number'
                        ? <AnimatedNumber value={s.numeric} />
                        : s.value}
                    {s.suffix && <span style={{ fontSize: 13, fontWeight: 400, marginLeft: 4, color: '#64748B' }}>{s.suffix}</span>}
                  </p>

                  {s.bar !== undefined && (
                    <div style={{ height: 5, borderRadius: 999, marginTop: 8, marginBottom: 4, overflow: 'hidden', background: '#F1F5F9' }}>
                      <div style={{ height: '100%', borderRadius: 999, transition: 'width 0.7s ease', width: `${s.bar}%`, background: s.accentColor }} />
                    </div>
                  )}

                  <p style={{ margin: '4px 0 0', fontSize: 12, color: '#64748B' }}>{s.sub}</p>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="dash-two-col">

          {/* ── Left column ───────────────────────────────────────────────── */}
          <div className="dash-col-left" style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

            {/* My Courses */}
            {!loading && (
              <div className="dash-card" style={{ animationDelay: '240ms' }}>
                <div style={{ background: '#FFFFFF', borderRadius: 12, overflow: 'hidden', border: '1px solid #E2E8F0', boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 4px 16px rgba(0,0,0,0.04)' }}>
                  <div className="dash-card-header" style={{ padding: '16px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #E2E8F0', gap: 8 }}>
                    <h2 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#0F172A', flexShrink: 0 }}>My study guides</h2>
                    <Link to="/courses" style={{ fontSize: 13, fontWeight: 600, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 4, color: '#1B3A5C', transition: 'color 0.12s', flexShrink: 0 }}
                      onMouseEnter={e => e.currentTarget.style.color='#C1272D'}
                      onMouseLeave={e => e.currentTarget.style.color='#1B3A5C'}>
                      <span className="dash-hdr-long">Browse study guides</span><span className="dash-hdr-short" style={{ display: 'none' }}>All</span> <Icon name="arrow" size={12} />
                    </Link>
                  </div>

                  {enrollments.length === 0 ? (
                    /* Empty state */
                    <div style={{ padding: '32px 24px', textAlign: 'center' }}>
                      <div style={{ width: 48, height: 48, borderRadius: 12, background: '#EFF6FF', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px' }}>
                        <Icon name="book" size={22} color="#1B3A5C" />
                      </div>
                      <p style={{ margin: '0 0 6px', fontSize: 14, fontWeight: 700, color: '#0F172A' }}>No study guides unlocked yet</p>
                      <p style={{ margin: '0 0 20px', fontSize: 13, color: '#64748B', lineHeight: 1.6 }}>
                        Browse the study guide library and unlock one to start learning.
                      </p>
                      <Link to="/courses"
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 7, background: '#1B3A5C', color: '#fff', textDecoration: 'none', padding: '9px 18px', borderRadius: 8, fontSize: 13, fontWeight: 700, transition: 'background 0.15s' }}
                        onMouseEnter={e => e.currentTarget.style.background='#142d47'}
                        onMouseLeave={e => e.currentTarget.style.background='#1B3A5C'}>
                        Browse study guides <Icon name="arrow" size={13} />
                      </Link>
                    </div>
                  ) : (
                    <>
                    {enrollments.slice(0, 5).map((enr, idx) => {
                      const shownCount = Math.min(enrollments.length, 5);
                      const pct = enr.chapters_total > 0 ? Math.round((enr.chapters_complete / enr.chapters_total) * 100) : 0;
                      const done = enr.chapters_complete >= enr.chapters_total && enr.chapters_total > 0;
                      return (
                        <div key={enr.id} className="dash-card-row" style={{ padding: '14px 20px', display: 'flex', alignItems: 'center', gap: 14, borderBottom: idx < shownCount - 1 ? '1px solid #E2E8F0' : 'none' }}>
                          <div style={{ position: 'relative', flexShrink: 0, width: 52, height: 52 }}>
                            <ProgressRing pct={pct} size={52} stroke={5} color={done ? '#059669' : '#1B3A5C'} />
                            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              {done
                                ? <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth={3} strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>
                                : <span style={{ fontSize: 10, fontWeight: 700, color: '#1B3A5C' }}>{pct}%</span>
                              }
                            </div>
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <p style={{ margin: '0 0 3px', fontWeight: 700, fontSize: 13, color: '#0F172A', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {enr.course_name}
                            </p>
                            <p style={{ margin: 0, fontSize: 12, color: '#64748B' }}>
                              {enr.chapters_complete}/{enr.chapters_total} chapters complete
                              {enr.certificate_code && <span style={{ color: '#059669', fontWeight: 600, marginLeft: 8 }}>· Certificate earned</span>}
                            </p>
                          </div>
                          <button onClick={() => window.open(`/courses/learn/${enr.course_id}`, '_blank', 'noopener,noreferrer')}
                            className="dash-card-row-btn"
                            style={{ flexShrink: 0, padding: '7px 14px', background: done ? '#f0fdf4' : '#1B3A5C', color: done ? '#059669' : '#fff', border: done ? '1px solid #86efac' : 'none', borderRadius: 8, fontWeight: 700, fontSize: 12, cursor: 'pointer', transition: 'all 0.12s', fontFamily: 'inherit' }}
                            onMouseEnter={e => { if (!done) e.currentTarget.style.background='#142d47'; }}
                            onMouseLeave={e => { if (!done) e.currentTarget.style.background='#1B3A5C'; }}>
                            {done ? 'Review' : 'Continue'}
                          </button>
                        </div>
                      );
                    })}
                    {enrollments.length > 5 && (
                      <div style={{ padding: '12px 20px', textAlign: 'center', borderTop: '1px solid #E2E8F0' }}>
                        <Link to="/courses" style={{ fontSize: 13, fontWeight: 600, color: '#1B3A5C', textDecoration: 'none' }}>
                          View all {enrollments.length} study guides →
                        </Link>
                      </div>
                    )}
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Active subscriptions */}
            {!loading && subscriptions.length > 0 && (
              <div className="dash-card" style={{ animationDelay: '260ms' }}>
                <div style={{ background: '#FFFFFF', borderRadius: 12, overflow: 'hidden', border: '1px solid #E2E8F0', boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 4px 16px rgba(0,0,0,0.04)' }}>
                  <div className="dash-card-header" style={{ padding: '16px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #E2E8F0', gap: 8 }}>
                    <h2 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#0F172A', flexShrink: 0 }}>Your active roles</h2>
                    <Link to="/roles"
                      style={{ fontSize: 13, fontWeight: 600, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 4, color: '#1B3A5C', cursor: 'pointer', transition: 'color 0.12s', flexShrink: 0 }}
                      onMouseEnter={e => e.currentTarget.style.color='#C1272D'}
                      onMouseLeave={e => e.currentTarget.style.color='#1B3A5C'}>
                      <span className="dash-hdr-long">Browse more</span><span className="dash-hdr-short" style={{ display: 'none' }}>More</span> <Icon name="arrow" size={12} />
                    </Link>
                  </div>
                  <div>
                    {subscriptions.map((sub, idx) => {
                      const isGlobal  = sub.tier === 'full_access' || sub.plan_type === 'job_seeker' || !sub.job_role_id;
                      const daysLeft  = sub.ends_at
                        ? Math.ceil((new Date(sub.ends_at) - Date.now()) / (1000 * 60 * 60 * 24))
                        : null;
                      const expiring  = daysLeft !== null && daysLeft <= 7 && daysLeft > 0;
                      const expired   = daysLeft !== null && daysLeft <= 0;

                      // Full Access / Job Seeker — no specific role linked
                      if (isGlobal) {
                        const planLabel = sub.tier === 'full_access' || sub.plan_type === 'full_access'
                          ? 'Full Access'
                          : sub.plan_type === 'job_seeker'
                            ? 'Job Seeker'
                            : 'All Roles';
                        const total = 40;
                        return (
                          <div key={sub.id}>
                            {/* Plan banner */}
                            {(expiring || expired) && (
                              <div style={{ padding: '8px 20px', background: expired ? '#FEE2E2' : '#FEF3C7', display: 'flex', alignItems: 'center', gap: 8 }}>
                                <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke={expired ? '#C1272D' : '#D97706'} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                                <span style={{ fontSize: 12, fontWeight: 600, color: expired ? '#C1272D' : '#92400E' }}>
                                  {expired ? 'Your plan has expired — ' : `Access expires in ${daysLeft} day${daysLeft !== 1 ? 's' : ''} — `}
                                </span>
                                <Link to="/subscribe/full-access" style={{ fontSize: 12, fontWeight: 700, color: expired ? '#C1272D' : '#D97706', textDecoration: 'underline' }}>Renew now</Link>
                              </div>
                            )}
                            <div style={{ padding: '14px 20px', display: 'flex', alignItems: 'center', gap: 16, background: '#F8FAFC', borderBottom: '1px solid #E2E8F0' }}>
                              <div style={{ width: 40, height: 40, borderRadius: 10, background: 'linear-gradient(135deg,#1B3A5C,#2A5280)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round">
                                  <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
                                </svg>
                              </div>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <p style={{ margin: '0 0 1px', fontSize: 13, fontWeight: 700, color: '#0F172A' }}>{planLabel} — All roles unlocked</p>
                                <p style={{ margin: 0, fontSize: 11, color: '#64748B' }}>
                                  Browse any role and your progress saves automatically
                                  {daysLeft !== null && daysLeft > 0 && (
                                    <span style={{ marginLeft: 6, fontWeight: 600, color: '#1B3A5C' }}>· {daysLeft} days left</span>
                                  )}
                                </p>
                              </div>
                              <Link to="/roles"
                                style={{ fontSize: 12, fontWeight: 600, color: '#1B3A5C', textDecoration: 'none', flexShrink: 0, transition: 'color 0.12s' }}
                                onMouseEnter={e => e.currentTarget.style.color='#C1272D'}
                                onMouseLeave={e => e.currentTarget.style.color='#1B3A5C'}>
                                Browse all →
                              </Link>
                            </div>

                            {/* In-progress roles for this global plan */}
                            {progress.length > 0 ? (
                              progress.slice(0, 5).map((rp, rpIdx) => {
                                const completed = rp.sub_chapters_completed || 0;
                                const pct       = Math.min(Math.round((completed / total) * 100), 100);
                                const remaining = total - completed;
                                return (
                                  <div key={rp.role_id} className="dash-card-row" style={{ padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 16, borderBottom: rpIdx < Math.min(progress.length, 5) - 1 ? '1px solid #E2E8F0' : 'none' }}>
                                    <div style={{ position: 'relative', flexShrink: 0, width: 64, height: 64 }}>
                                      <ProgressRing pct={pct} size={64} stroke={6} color="#1B3A5C" />
                                      <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <span style={{ fontSize: 11, fontWeight: 700, color: '#1B3A5C' }}>{pct}%</span>
                                      </div>
                                    </div>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                      <p style={{ margin: '0 0 2px', fontSize: 15, fontWeight: 700, color: '#0F172A', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{rp.role_title}</p>
                                      <p style={{ margin: '0 0 8px', fontSize: 12, color: '#64748B' }}>
                                        {completed}/{total} sub-chapters
                                        {remaining > 0 && <span style={{ marginLeft: 8, fontWeight: 600, color: '#1B3A5C' }}>· {remaining} left</span>}
                                      </p>
                                      <div style={{ height: 5, borderRadius: 999, overflow: 'hidden', background: '#F1F5F9' }}>
                                        <div style={{ height: '100%', borderRadius: 999, transition: 'width 0.7s ease', width: `${pct}%`, background: 'linear-gradient(90deg, #1B3A5C, #2A5280)' }} />
                                      </div>
                                    </div>
                                    <Link to={`/roles/${rp.slug}`}
                                      className="dash-card-row-btn"
                                      style={{ background: '#1B3A5C', color: '#fff', textDecoration: 'none', padding: '8px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600, flexShrink: 0, transition: 'background 0.15s', cursor: 'pointer' }}
                                      onMouseEnter={e => e.currentTarget.style.background='#142d47'}
                                      onMouseLeave={e => e.currentTarget.style.background='#1B3A5C'}>
                                      Continue →
                                    </Link>
                                  </div>
                                );
                              })
                            ) : (
                              <div style={{ padding: '24px 20px', textAlign: 'center' }}>
                                <p style={{ margin: '0 0 4px', fontSize: 13, fontWeight: 600, color: '#0F172A' }}>No roles started yet</p>
                                <p style={{ margin: '0 0 14px', fontSize: 12, color: '#64748B' }}>Pick any role to start — your progress will appear here.</p>
                                <Link to="/roles"
                                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#1B3A5C', color: '#fff', textDecoration: 'none', padding: '8px 16px', borderRadius: 8, fontSize: 13, fontWeight: 700, transition: 'background 0.15s' }}
                                  onMouseEnter={e => e.currentTarget.style.background='#142d47'}
                                  onMouseLeave={e => e.currentTarget.style.background='#1B3A5C'}>
                                  Browse roles →
                                </Link>
                              </div>
                            )}
                            {progress.length > 5 && (
                              <div style={{ padding: '12px 20px', textAlign: 'center', borderTop: '1px solid #E2E8F0' }}>
                                <Link to="/roles" style={{ fontSize: 13, fontWeight: 600, color: '#1B3A5C', textDecoration: 'none' }}>
                                  View all {progress.length} roles in progress →
                                </Link>
                              </div>
                            )}
                          </div>
                        );
                      }

                      // Per-role subscription
                      const rp        = progress.find(p => p.role_id === sub.job_role_id);
                      const completed = rp?.sub_chapters_completed || 0;
                      const total     = 40;
                      const pct       = Math.min(Math.round((completed / total) * 100), 100);
                      const remaining = total - completed;
                      return (
                        <div key={sub.id} style={{ borderBottom: idx < subscriptions.length - 1 ? '1px solid #E2E8F0' : 'none' }}>
                        {(expiring || expired) && (
                          <div style={{ padding: '8px 20px', background: expired ? '#FEE2E2' : '#FEF3C7', display: 'flex', alignItems: 'center', gap: 8 }}>
                            <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke={expired ? '#C1272D' : '#D97706'} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                            <span style={{ fontSize: 12, fontWeight: 600, color: expired ? '#C1272D' : '#92400E' }}>
                              {expired
                                ? `Access for ${sub.role_title || 'this role'} has expired — `
                                : `Access expires in ${daysLeft} day${daysLeft !== 1 ? 's' : ''} — `}
                            </span>
                            <Link to={`/subscribe/${sub.role_slug || ''}`} style={{ fontSize: 12, fontWeight: 700, color: expired ? '#C1272D' : '#D97706', textDecoration: 'underline' }}>
                              Renew now
                            </Link>
                          </div>
                        )}
                        <div className="dash-card-row" style={{ padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 16 }}>
                          <div style={{ position: 'relative', flexShrink: 0, width: 64, height: 64 }}>
                            <ProgressRing pct={pct} size={64} stroke={6} color="#1B3A5C" />
                            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <span style={{ fontSize: 11, fontWeight: 700, color: '#1B3A5C' }}>{pct}%</span>
                            </div>
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <p style={{ margin: '0 0 2px', fontSize: 15, fontWeight: 700, color: '#0F172A', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{sub.role_title}</p>
                            <p style={{ margin: '0 0 8px', fontSize: 12, color: '#64748B' }}>
                              {completed}/{total} sub-chapters
                              {remaining > 0 && (
                                <span style={{ marginLeft: 8, fontWeight: 600, color: '#1B3A5C' }}>· {remaining} left</span>
                              )}
                            </p>
                            <div style={{ height: 5, borderRadius: 999, overflow: 'hidden', background: '#F1F5F9' }}>
                              <div style={{ height: '100%', borderRadius: 999, transition: 'width 0.7s ease', width: `${pct}%`, background: 'linear-gradient(90deg, #1B3A5C, #2A5280)' }} />
                            </div>
                          </div>
                          <Link to={`/roles/${sub.role_slug}`}
                            className="dash-card-row-btn"
                            style={{ background: '#1B3A5C', color: '#fff', textDecoration: 'none', padding: '8px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600, flexShrink: 0, transition: 'background 0.15s', cursor: 'pointer' }}
                            onMouseEnter={e => e.currentTarget.style.background='#142d47'}
                            onMouseLeave={e => e.currentTarget.style.background='#1B3A5C'}>
                            Continue →
                          </Link>
                        </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {/* Recent activity */}
            {!loading && progress.length > 0 && (
              <div className="dash-card"
                style={{ background: '#FFFFFF', borderRadius: 12, overflow: 'hidden', border: '1px solid #E2E8F0', boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 4px 16px rgba(0,0,0,0.04)', animationDelay: '320ms' }}>
                <div style={{ padding: '16px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #E2E8F0' }}>
                  <h2 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#0F172A' }}>Recent activity</h2>
                  <span style={{ fontSize: 12, color: '#64748B', fontWeight: 500 }}>{progress.length} role{progress.length !== 1 ? 's' : ''}</span>
                </div>
                {progress.slice(0, 5).map((p, i) => {
                  const scorePct  = p.avg_score / 25 * 100;
                  const scoreColor = scorePct >= 80 ? '#059669' : scorePct >= 60 ? '#2563EB' : '#D97706';
                  const scoreBg    = scorePct >= 80 ? '#D1FAE5' : scorePct >= 60 ? '#EFF6FF' : '#FEF3C7';
                  return (
                    <div key={p.role_id}
                      style={{ padding: '12px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: i < Math.min(progress.length, 5) - 1 ? '1px solid #F8FAFC' : 'none', transition: 'background 0.12s' }}
                      onMouseEnter={e => e.currentTarget.style.background='#F8FAFC'}
                      onMouseLeave={e => e.currentTarget.style.background='transparent'}>
                      <div style={{ flex: 1, minWidth: 0, marginRight: 16 }}>
                        <p style={{ margin: '0 0 2px', fontSize: 14, fontWeight: 600, color: '#0F172A', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.role_title}</p>
                        <p style={{ margin: 0, fontSize: 12, color: '#475569' }}>{p.sub_chapters_completed} sub-chapters completed</p>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
                        <span style={{ fontSize: 12, padding: '3px 10px', borderRadius: 6, fontWeight: 700, background: scoreBg, color: scoreColor }}>
                          {Math.round(p.avg_score)}/25
                        </span>
                        <Link to={`/roles/${p.slug}`}
                          style={{ fontSize: 13, fontWeight: 600, textDecoration: 'none', color: '#1B3A5C', transition: 'color 0.12s', cursor: 'pointer' }}
                          onMouseEnter={e => e.currentTarget.style.color='#C1272D'}
                          onMouseLeave={e => e.currentTarget.style.color='#1B3A5C'}>
                          View →
                        </Link>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Activity heatmap */}
            {!loading && streak && progress.length > 0 && (
              <div className="dash-card"
                style={{ background: '#FFFFFF', borderRadius: 12, padding: '20px', border: '1px solid #E2E8F0', boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 4px 16px rgba(0,0,0,0.04)', animationDelay: '380ms' }}>
                <h2 style={{ margin: '0 0 16px', fontSize: 15, fontWeight: 700, color: '#0F172A' }}>Practice history</h2>
                <ActivityHeatmap data={streak.daily_scores || {}} />
              </div>
            )}

            {/* Empty state */}
            {!loading && subscriptions.length === 0 && progress.length === 0 && (
              <div className="dash-card"
                style={{ background: '#FFFFFF', borderRadius: 12, padding: '64px 32px', textAlign: 'center', border: '1px solid #E2E8F0', boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 4px 16px rgba(0,0,0,0.04)', animationDelay: '200ms' }}>
                <div style={{ margin: '0 auto 24px', width: 96, height: 96 }}>
                  <svg viewBox="0 0 96 96" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="48" cy="48" r="48" fill="#F1F5F9" />
                    <rect x="24" y="28" width="48" height="40" rx="6" fill="#E2E8F0" />
                    <rect x="30" y="35" width="36" height="4" rx="2" fill="#1B3A5C" opacity="0.4" />
                    <rect x="30" y="43" width="26" height="3" rx="1.5" fill="#1B3A5C" opacity="0.25" />
                    <rect x="30" y="49" width="30" height="3" rx="1.5" fill="#1B3A5C" opacity="0.2" />
                    <circle cx="66" cy="62" r="12" fill="#C1272D" />
                    <path d="M62 62l2.5 2.5L70 57" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                <h2 style={{ margin: '0 0 8px', fontSize: 21, fontWeight: 800, color: '#0F172A', letterSpacing: '-0.3px' }}>
                  Your first practice session is just one click away
                </h2>
                <p style={{ margin: '0 0 32px', fontSize: 14, lineHeight: 1.75, maxWidth: 360, marginLeft: 'auto', marginRight: 'auto', color: '#475569' }}>
                  Pick any role. Get 200 free questions with full AI feedback — no payment, no account upgrade needed.
                </p>
                <Link to="/roles"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: '#C1272D', color: '#fff', textDecoration: 'none', padding: '12px 28px', borderRadius: 8, fontSize: 15, fontWeight: 700, transition: 'background 0.15s', cursor: 'pointer' }}
                  onMouseEnter={e => e.currentTarget.style.background='#A8202B'}
                  onMouseLeave={e => e.currentTarget.style.background='#C1272D'}>
                  Browse roles <Icon name="arrow" size={15} />
                </Link>
              </div>
            )}
          </div>

          {/* ── Right column ──────────────────────────────────────────────── */}
          <div className="dash-col-right" style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

            {/* Daily challenge */}
            {features.daily_challenge && challenge && (
              <DailyChallengeCard
                challenge={challenge}
                done={challengeDone}
                selected={challengeSel}
                onSelect={setChallengeSel}
                onSubmit={async () => {
                  if (challengeSel === null) return;
                  const r = await completeDailyChallenge(challenge.question_id, challengeSel);
                  setChallenge(c => ({ ...c, completed: true, was_correct: r.data.was_correct, correct_answer: r.data.correct_answer }));
                  setChallengeDone(true);
                }}
              />
            )}

            {/* JD Prep premium card */}
            <div className="dash-card"
              style={{
                borderRadius: 12, overflow: 'hidden',
                background: 'linear-gradient(160deg, #C1272D 0%, #9b1f24 55%, #7a1a1e 100%)',
                boxShadow: '0 4px 20px rgba(193,39,45,0.3)',
                animationDelay: '200ms', position: 'relative',
              }}>
              <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', background: 'radial-gradient(circle at 80% 20%, rgba(255,255,255,0.08) 0%, transparent 55%)' }} />
              <div style={{ position: 'relative', padding: '24px 20px', color: '#fff' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                  <div style={{ width: 40, height: 40, borderRadius: 10, background: 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Icon name="sparkle" size={18} color="#fff" />
                  </div>
                  <span style={{ fontSize: 11, fontWeight: 700, background: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.25)', padding: '3px 10px', borderRadius: 999, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    2 FREE
                  </span>
                </div>
                <h3 style={{ margin: '0 0 8px', fontSize: 16, fontWeight: 700 }}>JD Interview Prep</h3>
                <p style={{ margin: '0 0 20px', fontSize: 13, lineHeight: 1.7, color: 'rgba(255,255,255,0.72)' }}>
                  Paste any job description. Get 125 AI-tailored questions across 5 chapters in minutes — plus your personalised improvement plan.
                </p>
                <Link to="/jd-prep"
                  style={{ display: 'block', textAlign: 'center', background: '#fff', color: '#C1272D', textDecoration: 'none', fontSize: 13, fontWeight: 700, padding: '10px 16px', borderRadius: 8, transition: 'opacity 0.15s', cursor: 'pointer' }}
                  onMouseEnter={e => e.currentTarget.style.opacity='0.9'}
                  onMouseLeave={e => e.currentTarget.style.opacity='1'}>
                  Generate 125 questions →
                </Link>
              </div>
            </div>

            {/* Recent JD prep sessions */}
            {!loading && jdSessions.length > 0 && (
              <div className="dash-card"
                style={{ background: '#FFFFFF', borderRadius: 12, overflow: 'hidden', border: '1px solid #E2E8F0', boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 4px 16px rgba(0,0,0,0.04)', animationDelay: '245ms' }}>
                <div style={{ padding: '14px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #E2E8F0' }}>
                  <h2 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: '#0F172A' }}>Recent JD prep</h2>
                  <Link to="/jd-prep"
                    style={{ fontSize: 12, fontWeight: 600, textDecoration: 'none', color: '#1B3A5C', cursor: 'pointer', transition: 'color 0.12s' }}
                    onMouseEnter={e => e.currentTarget.style.color='#C1272D'}
                    onMouseLeave={e => e.currentTarget.style.color='#1B3A5C'}>
                    View all →
                  </Link>
                </div>
                <div>
                  {jdSessions.map((s, idx) => (
                    <Link key={s.id} to={`/jd-prep/${s.id}`}
                      style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 20px', textDecoration: 'none', borderBottom: idx < jdSessions.length - 1 ? '1px solid #F8FAFC' : 'none', transition: 'background 0.12s', cursor: 'pointer', gap: 12 }}
                      onMouseEnter={e => e.currentTarget.style.background='#F8FAFC'}
                      onMouseLeave={e => e.currentTarget.style.background='transparent'}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ margin: '0 0 2px', fontSize: 13, fontWeight: 600, color: '#0F172A', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {s.job_title || 'Untitled role'}{s.company_name ? ` — ${s.company_name}` : ''}
                        </p>
                        <p style={{ margin: 0, fontSize: 11, color: '#64748B' }}>
                          {s.chapter_count || 5} chapters · {new Date(s.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                        </p>
                      </div>
                      <span style={{ fontSize: 11, color: '#1B3A5C', flexShrink: 0, fontWeight: 600 }}>View →</span>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Explore roles */}
            {!loading && suggestedRoles.length > 0 && (
              <div className="dash-card"
                style={{ background: '#FFFFFF', borderRadius: 12, overflow: 'hidden', border: '1px solid #E2E8F0', boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 4px 16px rgba(0,0,0,0.04)', animationDelay: '290ms' }}>
                <div style={{ padding: '14px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #E2E8F0' }}>
                  <h2 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: '#0F172A' }}>Explore roles</h2>
                  <Link to="/roles"
                    style={{ fontSize: 12, fontWeight: 600, textDecoration: 'none', color: '#1B3A5C', cursor: 'pointer', transition: 'color 0.12s' }}
                    onMouseEnter={e => e.currentTarget.style.color='#C1272D'}
                    onMouseLeave={e => e.currentTarget.style.color='#1B3A5C'}>
                    All roles →
                  </Link>
                </div>
                <div>
                  {suggestedRoles.map((role, idx) => (
                    <Link key={role.id} to={`/roles/${role.slug}`}
                      style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px', textDecoration: 'none', borderBottom: idx < suggestedRoles.length - 1 ? '1px solid #F8FAFC' : 'none', transition: 'background 0.12s', cursor: 'pointer' }}
                      onMouseEnter={e => e.currentTarget.style.background='#F8FAFC'}
                      onMouseLeave={e => e.currentTarget.style.background='transparent'}>
                      <div style={{ minWidth: 0 }}>
                        <p style={{ margin: '0 0 2px', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: '#C1272D' }}>{role.category}</p>
                        <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: '#0F172A', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{role.title}</p>
                      </div>
                      <Icon name="arrow" size={15} color="#64748B" />
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Upcoming Interviews widget */}
            {!loading && (
              <div className="dash-card" style={{ background: '#fff', borderRadius: 12, border: '1px solid #E2E8F0', padding: '16px 18px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', animationDelay: '320ms' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                  <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: '#0F172A', display: 'flex', alignItems: 'center', gap: 7 }}>
                    <span style={{ width: 22, height: 22, borderRadius: 6, background: '#EFF6FF', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Icon name="calendar" size={13} color="#1B3A5C" />
                    </span>
                    Upcoming Interviews
                  </p>
                  <Link to="/my-interviews" style={{ fontSize: 11, fontWeight: 600, color: '#1B3A5C', textDecoration: 'none' }}>
                    Manage →
                  </Link>
                </div>
                {upcomingIvs.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '6px 0' }}>
                    <p style={{ margin: '0 0 8px', fontSize: 12, color: '#94A3B8' }}>No upcoming interviews logged</p>
                    <Link to="/my-interviews" style={{ fontSize: 12, fontWeight: 700, color: '#1B3A5C', textDecoration: 'none', background: '#EFF6FF', padding: '5px 14px', borderRadius: 8 }}>
                      + Log an interview
                    </Link>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {upcomingIvs.map(iv => {
                      const d    = new Date(iv.interview_date);
                      const days = Math.round((d - new Date(new Date().toDateString())) / 86400000);
                      const chip = days === 0 ? ['Today!','#C1272D','#FEF2F2'] : days === 1 ? ['Tomorrow','#D97706','#FFFBEB'] : [`${days}d away`,'#16A34A','#F0FDF4'];
                      return (
                        <div key={iv.id} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <p style={{ margin: 0, fontSize: 12, fontWeight: 600, color: '#0F172A', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {iv.company_name || 'Interview'}{iv.role_title ? ` — ${iv.role_title}` : ''}
                            </p>
                            <p style={{ margin: 0, fontSize: 11, color: '#64748B' }}>
                              {d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' })}
                            </p>
                          </div>
                          <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20, background: chip[2], color: chip[1], flexShrink: 0 }}>{chip[0]}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* ATS Score CTA */}
            {!loading && (
              <div className="dash-card" style={{ background: '#FFFFFF', borderRadius: 12, padding: '16px 20px', border: '1px solid #E2E8F0', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', animationDelay: '340ms' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                  <div style={{ width: 32, height: 32, borderRadius: 8, background: '#F0FDF4', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 2 }}>
                    <svg width={15} height={15} viewBox="0 0 24 24" fill="none" stroke="#16A34A" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/>
                    </svg>
                  </div>
                  <div>
                    <p style={{ margin: '0 0 4px', fontSize: 13, fontWeight: 700, color: '#0F172A' }}>ATS CV Score</p>
                    <p style={{ margin: '0 0 8px', fontSize: 12, lineHeight: 1.65, color: '#475569' }}>
                      Get your CV scored by AI — see keyword gaps and targeted improvements.
                    </p>
                    <Link to="/ats"
                      style={{ fontSize: 12, fontWeight: 600, textDecoration: 'none', color: '#16A34A', transition: 'color 0.12s', cursor: 'pointer' }}
                      onMouseEnter={e => e.currentTarget.style.color='#166534'}
                      onMouseLeave={e => e.currentTarget.style.color='#16A34A'}>
                      Score my CV →
                    </Link>
                  </div>
                </div>
              </div>
            )}

            {/* Certificates CTA */}
            {!loading && (
              <div className="dash-card"
                style={{ background: '#FFFFFF', borderRadius: 12, padding: '16px 20px', border: '1px solid #E2E8F0', boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 4px 16px rgba(0,0,0,0.04)', animationDelay: '340ms' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                  <div style={{ width: 32, height: 32, borderRadius: 8, background: '#EFF6FF', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 2 }}>
                    <Icon name="trophy" size={15} color="#2563EB" />
                  </div>
                  <div>
                    <p style={{ margin: '0 0 4px', fontSize: 13, fontWeight: 700, color: '#0F172A' }}>Earn your certificate</p>
                    <p style={{ margin: '0 0 8px', fontSize: 12, lineHeight: 1.65, color: '#475569' }}>
                      Complete all 40 sub-chapters in any role to earn a shareable certificate of completion.
                    </p>
                    <Link to="/certificates"
                      style={{ fontSize: 12, fontWeight: 600, textDecoration: 'none', color: '#1B3A5C', transition: 'color 0.12s', cursor: 'pointer' }}
                      onMouseEnter={e => e.currentTarget.style.color='#C1272D'}
                      onMouseLeave={e => e.currentTarget.style.color='#1B3A5C'}>
                      View certificates →
                    </Link>
                  </div>
                </div>
              </div>
            )}

            {/* AI Tools CTA */}
            {!loading && (() => {
              const isFullAccess = subscriptions?.some(s => ['full_access','job_seeker'].includes(s.plan_type) || ['full_access','job_seeker'].includes(s.tier));
              const isJobSeeker  = subscriptions?.some(s => s.plan_type === 'job_seeker' || s.tier === 'job_seeker');
              const tools = [
                { to: '/star-coach',    label: 'STAR Coach',         locked: !isFullAccess, tier: 'Full Access' },
                { to: '/cover-letter',  label: 'Cover Letter',        locked: !isFullAccess, tier: 'Full Access' },
                { to: '/company-prep',  label: 'Company Prep',        locked: !isFullAccess, tier: 'Full Access' },
                { to: '/debrief',       label: 'Interview Debrief',   locked: !isFullAccess, tier: 'Full Access' },
                { to: '/linkedin-opt',  label: 'LinkedIn Optimiser',  locked: !isJobSeeker,  tier: 'Job Seeker' },
                { to: '/salary-neg',    label: 'Salary Negotiation',  locked: !isJobSeeker,  tier: 'Job Seeker' },
                { to: '/readiness-report', label: 'Readiness Report', locked: !isJobSeeker,  tier: 'Job Seeker' },
              ];
              return (
                <div className="dash-card"
                  style={{ background: '#FFFFFF', borderRadius: 12, overflow: 'hidden', border: '1px solid #E2E8F0', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', animationDelay: '360ms' }}>
                  <div style={{ padding: '14px 20px', borderBottom: '1px solid #E2E8F0', display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ width: 28, height: 28, borderRadius: 8, background: '#F5F3FF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="#7C3AED" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
                      </svg>
                    </div>
                    <h2 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: '#0F172A' }}>AI Tools</h2>
                  </div>
                  <div>
                    {tools.map((t, idx) => {
                      const el = t.locked ? (
                        <div key={t.to} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '11px 20px', borderBottom: idx < tools.length - 1 ? '1px solid #F8FAFC' : 'none', opacity: 0.55, cursor: 'default' }}>
                          <span style={{ fontSize: 13, color: '#475569', fontWeight: 500 }}>{t.label}</span>
                          <span style={{ fontSize: 10, fontWeight: 700, background: '#F1F5F9', color: '#64748B', padding: '2px 8px', borderRadius: 999, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{t.tier}</span>
                        </div>
                      ) : (
                        <Link key={t.to} to={t.to}
                          style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '11px 20px', textDecoration: 'none', borderBottom: idx < tools.length - 1 ? '1px solid #F8FAFC' : 'none', transition: 'background 0.12s', cursor: 'pointer' }}
                          onMouseEnter={e => e.currentTarget.style.background='#F8FAFC'}
                          onMouseLeave={e => e.currentTarget.style.background='transparent'}>
                          <span style={{ fontSize: 13, fontWeight: 600, color: '#0F172A' }}>{t.label}</span>
                          <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="#94A3B8" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="9 18 15 12 9 6"/>
                          </svg>
                        </Link>
                      );
                      return el;
                    })}
                  </div>
                  {!isFullAccess && (
                    <div style={{ padding: '10px 20px', borderTop: '1px solid #F1F5F9', background: '#FAFAFA' }}>
                      <Link to="/pricing"
                        style={{ fontSize: 12, fontWeight: 600, color: '#7C3AED', textDecoration: 'none', transition: 'color 0.12s' }}
                        onMouseEnter={e => e.currentTarget.style.color='#6D28D9'}
                        onMouseLeave={e => e.currentTarget.style.color='#7C3AED'}>
                        Upgrade to unlock AI Tools →
                      </Link>
                    </div>
                  )}
                </div>
              );
            })()}

          </div>
        </div>
      </div>
    </div>
  );
}
